<template lang="pug">
.briq-multiselect.briq-form-element(:class="{pending}")
  label.input-label(v-if="label" :for="name") {{ label }}
  briq-required-and-optional-markers(
    :isRequired="isRequired"
    :isDisabled="$attrs.disabled || $attrs.readonly"
    :hideOptionalMarker="hideOptionalMarker"
    :label="label"
    )
  briq-tooltip(:help="help")
  multiselect(
    v-bind="attrs"
    v-on="listeners"
    v-model="innerValue"
    :name="name"
    :options="options"
    :deselect-label="deselectLabel"
    :allow-empty="!required"
    :label="display"
    :style="style"
    )
    template(v-for="slot in Object.keys($scopedSlots)" :slot="slot" slot-scope="scope")
      slot(:name="slot" v-bind="scope")
  button.toggle-all-button(v-if="canToggleAll" @click="toggleAll" :data-test-id="generateTestId(`toggle-all-${label}`)") {{ toggleAllLabel }}
  input(type="hidden" :name="name" :value="formValue")
  .validation-message(v-if="error" :data-test-id="`validation-message-${this.$attrs['data-test-id']}`") {{ error }}
</template>

<script>
import Multiselect from 'vue-multiselect'
import { pipe, map, includes, allPass } from 'ramda'
import toKebab from '@/helpers/toKebab.js'

export default {
  name: 'BriqMultiselect',
  inheritAttrs: false,
  components: { Multiselect },
  data() {
    return {
      innerValue: null,
      isMounted: false,
      deletedValue: null,
    }
  },
  props: {
    name: String,
    label: String,
    help: String,
    error: String,
    hideOptionalMarker: Boolean,
    options: { type: Array, required: true },
    value: [Object, String, Array, Number],
    valueSetter: Function,
    display: String, // if options are objects, which key should be displayed
    bindObject: Boolean, // if options are objects, should the bound value being emitted be the object?
    allowToggleAll: Boolean,
    minWidth: String,
    pending: Boolean,
  },
  computed: {
    attrs() {
      if (this.$attrs['track-by']) {
        return {
          ...this.$attrs,
          label: this.display || this.$attrs['track-by'],
        }
      }
      return this.$attrs
    },
    listeners() {
      const vm = this
      return {
        ...this.$listeners,
        input(x) {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          vm.innerValue = x
        },
      }
    },
    formValue() {
      return this.valueSetter && this.innerValue ? this.valueSetter(this.innerValue) : null
    },
    required() {
      return [true, 'required'].includes(this.$attrs.required)
    },
    isRequired() {
      return this.$attrs.required || this.required
    },
    deselectLabel() {
      return this.required === true ? '' : 'Press enter to remove'
    },
    canToggleAll() {
      return this.allowToggleAll && this.$attrs.multiple
    },
    toggleAllLabel() {
      return this.allAreChecked ? 'Select none' : 'Select all'
    },
    allAreChecked() {
      return pipe(map(includes), allPass)(this.options)(this.innerValue)
    },
    style() {
      return this.minWidth ? { minWidth: this.minWidth } : null
    },
  },
  methods: {
    setInnerValue(val) {
      if (this.$attrs['track-by'] && !this.bindObject) {
        this.innerValue = this.options.find(o => o[this.$attrs['track-by']] === val)
      } else this.innerValue = val
    },
    toggleAll() {
      if (!this.canToggleAll) return
      this.innerValue = this.allAreChecked ? [] : this.options
    },
    generateTestId(value) {
      return toKebab(value)
    },
  },
  beforeMount() {
    this.setInnerValue(this.value)
  },
  async mounted() {
    await this.$nextTick() // needed to prevent emit on mount
    this.isMounted = true
  },
  watch: {
    value(newVal) {
      this.setInnerValue(newVal)
    },
    innerValue(newVal) {
      if (!this.isMounted) return
      if (!newVal) return this.$emit('input', null)
      const emitVal =
        this.$attrs['track-by'] && !this.bindObject ? newVal[this.$attrs['track-by']] : newVal
      this.$emit('input', emitVal)
    },
  },
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped lang="stylus">
.input-label
  margin-right 4px

.toggle-all-button
  margin-top 7px
  text-decoration underline
  color #4e5a6a

.toggle-all-button:hover
  color black
</style>

<style lang="stylus">
.multiselect
  min-height 0
  // min-width 275px // does this not break anything?
  cursor pointer

  .multiselect__content
    padding 0px

span.multiselect__placeholder
  padding-top 0
  margin-bottom 0

.multiselect__input, .multiselect__single
  margin-top 1px
  background transparent

.multiselect--disabled, .multiselect--disabled .multiselect__select
  cursor not-allowed
  pointer-events auto
  opacity 1

  .multiselect__tags
    background transparent

.pending > .multiselect
  cursor wait

.multiselect__tags
  font-size 16px
  line-height 24px

.multiselect__option--selected
  background transparent

.multiselect__option--highlight, .multiselect__option--highlight:after, .multiselect__option--selected.multiselect__option--highlight, .multiselect__option--selected.multiselect__option--highlight:after
  background #F1F3F6
  color black

.multiselect__tag
  margin-bottom 0
  background #009BFF
  color white

.multiselect__tag-icon::after
  content '✖'
  color white

.multiselect__tag-icon:hover
  background #008ce8

.multiselect--active
  .multiselect__tags
    border-color #009bff
    box-shadow 0 2px 4px 0 rgba(0, 0, 0, 0.15)
</style>
